import { z } from 'zod';

import { chatUiBase } from '../../shared/schema/base.js';

/** Um lugar do agendamento de carro: o `ref` opaco que o agente resolve de volta
 *  (o lugar salvo, o resultado da busca), o nome e, se houver, o endereço. */
export const ChatUiMobilityPlannerPlaceSchema = z
  .object({
    ref: z.string().min(1).max(512),
    title: z.string().min(1).max(200),
    subtitle: z.string().min(1).max(300).optional(),
  })
  .strict();
export type ChatUiMobilityPlannerPlace = z.infer<typeof ChatUiMobilityPlannerPlaceSchema>;

/**
 * Uma ponta do traslado (origem ou destino) como o agente a deixou: o lugar já
 * escolhido, o que a pessoa disse e ninguém resolveu (`term`, o ponto de partida
 * da busca no campo) e até 4 candidatos que o agente achou e não soube escolher.
 */
export const ChatUiMobilityPlannerFieldSchema = z
  .object({
    selected: ChatUiMobilityPlannerPlaceSchema.optional(),
    term: z.string().trim().min(1).max(120).optional(),
    suggestions: z.array(ChatUiMobilityPlannerPlaceSchema).max(4).optional(),
  })
  .strict();
export type ChatUiMobilityPlannerField = z.infer<typeof ChatUiMobilityPlannerFieldSchema>;

/** O sentido da corrida: só a ida, ou a ida e a volta (duas corridas). */
export const MobilityPlannerTripTypeSchema = z.enum(['one_way', 'round_trip']);
export type MobilityPlannerTripType = z.infer<typeof MobilityPlannerTripTypeSchema>;

/** O dia (`YYYY-MM-DD`) e a hora (`HH:mm`) do agendamento, no relógio do embarque. */
const DaySchema = z.iso.date();
const ClockSchema = z.iso.time({ precision: -1 });

/** Quantas pessoas vão no carro: quem viaja e até 3 acompanhantes. */
export const MOBILITY_PLANNER_MAX_PASSENGERS = 4;
const PassengersSchema = z.number().int().min(1).max(MOBILITY_PLANNER_MAX_PASSENGERS);

/**
 * A volta da ida e volta como o agente a deixou. A rota nasce invertida (do
 * destino da ida à origem): `origin`/`destination` só vêm quando o agente já sabe
 * de outra ponta (o pouso em outro aeroporto). `when` é só leitura, como o da
 * ida, e `hint` diz de onde ele veio ("Horário sugerido pelo seu voo…").
 */
export const ChatUiMobilityPlannerReturnSchema = z
  .object({
    origin: ChatUiMobilityPlannerFieldSchema.optional(),
    destination: ChatUiMobilityPlannerFieldSchema.optional(),
    when: z.string().min(1).max(80).optional(),
    hint: z.string().min(1).max(200).optional(),
  })
  .strict();
export type ChatUiMobilityPlannerReturn = z.infer<typeof ChatUiMobilityPlannerReturnSchema>;

/**
 * A escolha de origem e destino do agendamento de carro NA CONVERSA
 * (`mobility_planner`): o agente não teve certeza de um dos lugares e pede para
 * a pessoa escolher. O Buscar devolve a busca por `interaction` (este challenge
 * + a `MobilityPlannerSearch`) — os lugares viajam como `ref`, nunca texto livre.
 * Sem `destination`, é o carro com motorista: só a origem importa.
 *
 * Com `tripType` (a corrida de cooperativa), o card pergunta antes da busca o
 * Sentido e as Pessoas — mudam preço e veículo — e, na ida e volta, mostra a
 * Volta em linha própria (`return`).
 *
 * Com `editableWhen`, o card vivo escolhe data e hora no Para quando; sem ele, é só leitura.
 */
export const MobilityPlannerBlockSchema = z
  .object({
    ...chatUiBase,
    kind: z.literal('mobility_planner'),
    challengeId: z.string().min(1),
    origin: ChatUiMobilityPlannerFieldSchema,
    destination: ChatUiMobilityPlannerFieldSchema.optional(),
    // O quando que o agente já entendeu ("amanhã, 09:00"), como texto.
    when: z.string().min(1).max(80).optional(),
    editableWhen: z
      .object({ date: DaySchema.optional(), time: ClockSchema.optional() })
      .strict()
      .optional(),
    // Só leitura: quem vai no carro, quando é outra pessoa ("Júlia Campos").
    who: z.string().min(1).max(120).optional(),
    tripType: MobilityPlannerTripTypeSchema.optional(),
    passengers: PassengersSchema.optional(),
    return: ChatUiMobilityPlannerReturnSchema.optional(),
  })
  .strict();
export type MobilityPlannerBlock = z.infer<typeof MobilityPlannerBlockSchema>;

/** A busca do card, ecoada em `interaction.data`: a origem e, quando o bloco
 *  tem destino, o destino. Com o Sentido no bloco, também o sentido e as
 *  pessoas; na ida e volta, as pontas da volta. Com o Quando editável, a data e
 *  a hora escolhidas. */
export const MobilityPlannerSearchSchema = z
  .object({
    action: z.literal('mobility_planner_search'),
    origin: ChatUiMobilityPlannerPlaceSchema,
    destination: ChatUiMobilityPlannerPlaceSchema.optional(),
    tripType: MobilityPlannerTripTypeSchema.optional(),
    passengers: PassengersSchema.optional(),
    when: z.object({ date: DaySchema, time: ClockSchema }).strict().optional(),
    return: z
      .object({
        origin: ChatUiMobilityPlannerPlaceSchema,
        destination: ChatUiMobilityPlannerPlaceSchema,
      })
      .strict()
      .optional(),
  })
  .strict()
  .refine((search) => (search.tripType === 'round_trip') === (search.return !== undefined), {
    message: 'a volta vem só (e sempre) na ida e volta',
    path: ['return'],
  });
export type MobilityPlannerSearch = z.infer<typeof MobilityPlannerSearchSchema>;

/**
 * Os lugares para um termo (a busca de endereço do agendamento de carro) —
 * leitura idempotente, chamada a cada tecla do campo. `ref` é o que o agente
 * resolve de volta; `address` nulo quando o lugar não tem endereço próprio.
 * Mora aqui, e não junto do hook, porque a raiz do pacote não carrega React.
 */
export interface MobilityPlacesPort {
  searchPlaces: (
    term: string,
  ) => Promise<readonly { ref: string; name: string; address: string | null }[]>;
}
